import clsx from "clsx";
import type { EmblaCarouselType, EmblaPluginType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import * as React from "react";

export function clamp(
  value: number,
  min: number | undefined,
  max: number | undefined,
) {
  if (min === undefined && max === undefined) {
    return value;
  }

  if (min !== undefined && max === undefined) {
    return Math.max(value, min);
  }

  if (min === undefined && max !== undefined) {
    return Math.min(value, max);
  }

  return Math.min(Math.max(value, min!), max!);
}

function getDefaultHeight(
  orientation: "horizontal" | "vertical",
  height: React.CSSProperties["height"],
) {
  if (height !== "auto") {
    return height;
  }

  return orientation === "vertical" ? 200 : "auto";
}

interface BaseCarouselProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  onNextSlide?: () => void;
  onPreviousSlide?: () => void;
  onSlideChange?: (index: number) => void;
  orientation?: "horizontal" | "vertical";
  align?: "start" | "center" | "end";
  slideGap?: number;
  slidesToScroll?: number | "auto";
  slidesPerView?: number;
  dragFree?: boolean;
  loop?: boolean;
  initialSlide?: number;
  inViewThreshold?: number;
  plugins?: EmblaPluginType[];
  skipSnaps?: boolean;
  containScroll?: false | "trimSnaps" | "keepSnaps";
  withKeyboardEvents?: boolean;
  height?: React.CSSProperties["height"];
  getCarouselApi?: (embla: EmblaCarouselType) => void;
  classNames?: {
    root?: string;
    viewport?: string;
    container?: string;
    indicators?: string;
    control?: string;
    controlPrev?: string;
    controlNext?: string;
    indicatorItem?: string;
  };
}

interface ControlsProps extends BaseCarouselProps {
  withControls?: true;
  nextControlIcon?: React.ReactNode;
  previousControlIcon?: React.ReactNode;
}

interface NonControlsProps extends BaseCarouselProps {
  withControls?: false;
  nextControlIcon?: never;
  previousControlIcon?: never;
}

interface IndicatorProps extends BaseCarouselProps {
  withIndicators?: true;
  indicatorVariant?: "line" | "dot";
}
interface NonIndicatorProps extends BaseCarouselProps {
  withIndicators?: false;
  indicatorVariant?: never;
}

type CarouselProps = (IndicatorProps | NonIndicatorProps) &
  (ControlsProps | NonControlsProps);

const CarouselContext = React.createContext<{
  slidesPerView: BaseCarouselProps["slidesPerView"];
  orientation: BaseCarouselProps["orientation"];
  slideGap: BaseCarouselProps["slideGap"];
}>({
  slidesPerView: 1,
  orientation: "horizontal",
  slideGap: 8,
});

const useCarouselContext = () => {
  const context = React.useContext(CarouselContext);
  if (context === undefined) {
    throw new Error(
      "useCarouselContext must be used within a CarouselProvider",
    );
  }
  return context;
};

interface CarouselItemProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const Carousel: React.FC<CarouselProps> & {
  Item: React.FC<CarouselItemProps>;
} = ({
  children,
  className,
  orientation = "horizontal",
  align = "center",
  slidesPerView = 1,
  slideGap = 8,
  slidesToScroll = 1,
  dragFree = false,
  loop = false,
  initialSlide = 0,
  inViewThreshold = 0,
  withControls = false,
  withIndicators = true,
  skipSnaps = false,
  containScroll = undefined,
  withKeyboardEvents = true,
  plugins = [],
  onSlideChange,
  onNextSlide,
  onPreviousSlide,
  nextControlIcon,
  previousControlIcon,
  indicatorVariant = "line",
  getCarouselApi,
  height = "auto",
  classNames,
  ...props
}) => {
  const [selected, setSelected] = React.useState(0);
  const [slidesCount, setSlidesCount] = React.useState(0);
  const [embla, emblaApi] = useEmblaCarousel(
    {
      axis: orientation === "horizontal" ? "x" : "y",
      direction: orientation === "horizontal" ? "ltr" : "rtl",
      startIndex: initialSlide,
      loop,
      align,
      slidesToScroll,
      dragFree,
      inViewThreshold,
      skipSnaps,
      containScroll,
    },
    plugins,
  );

  const handleSelect = React.useCallback(() => {
    if (!emblaApi) return;
    const slide = emblaApi.selectedScrollSnap();
    setSelected(slide);
    slide !== selected && onSlideChange?.(slide);
  }, [emblaApi, setSelected, onSlideChange, selected]);

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
    onPreviousSlide?.();
  }, [emblaApi]);

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
    onNextSlide?.();
  }, [emblaApi]);

  React.useEffect(() => {
    if (emblaApi) {
      getCarouselApi?.(emblaApi);
      handleSelect();
      setSlidesCount(emblaApi.scrollSnapList().length);
      emblaApi.on("select", handleSelect);

      return () => {
        emblaApi.off("select", handleSelect);
      };
    }

    return undefined;
  }, [emblaApi, slidesToScroll, handleSelect]);

  React.useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit();
      setSlidesCount(emblaApi.scrollSnapList().length);
      setSelected((currentSelected) =>
        clamp(currentSelected, 0, React.Children.toArray(children).length - 1),
      );
    }
  }, [React.Children.toArray(children).length, slidesToScroll]);

  const canScrollPrev = emblaApi?.canScrollPrev() || false;
  const canScrollNext = emblaApi?.canScrollNext() || false;

  const handleKeydown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (withKeyboardEvents) {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        }
      }
    },
    [emblaApi],
  );
  const handleScroll = React.useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const indicators = Array(slidesCount)
    .fill(0)
    .map((_, index) => (
      <button
        key={index}
        data-active={index === selected || undefined}
        aria-hidden
        tabIndex={-1}
        onClick={() => handleScroll(index)}
        data-orientation={orientation}
        data-indicator-variant={indicatorVariant}
        className={clsx(
          "ox-reset ox-CarouselIndicatorItem",
          classNames?.indicatorItem,
        )}
      />
    ));

  const defaultHeight = getDefaultHeight(orientation, height);

  return (
    <CarouselContext.Provider value={{ slidesPerView, orientation, slideGap }}>
      <div
        onKeyDownCapture={handleKeydown}
        className={clsx("ox-reset ox-Carousel", className, classNames?.root)}
      >
        <div
          className={clsx("ox-reset ox-Viewport", classNames?.viewport)}
          data-orientation={orientation}
          ref={embla}
          {...props}
        >
          <div
            className={clsx(
              "ox-reset ox-CarouselContainer",
              classNames?.container,
            )}
            data-orientation={orientation}
            style={{
              height: defaultHeight,
              marginBottom:
                orientation === "vertical"
                  ? `calc(${slideGap}px * -1)`
                  : undefined,
              marginInlineEnd:
                orientation === "horizontal"
                  ? `calc(${slideGap}px * -1)`
                  : undefined,
            }}
          >
            {children}
          </div>
        </div>

        {withIndicators ? (
          <div
            className={clsx(
              "ox-reset ox-CarouselIndicators",
              classNames?.indicators,
            )}
            data-orientation={orientation}
          >
            {indicators}
          </div>
        ) : null}

        {withControls ? (
          <div
            className={clsx("ox-reset ox-CarouselControl", classNames?.control)}
            {...props}
            data-orientation={orientation}
          >
            <button
              className={clsx(
                "ox-reset ox-CarouselControlPrev",
                classNames?.controlPrev,
              )}
              onClick={scrollPrev}
              disabled={!canScrollPrev || undefined}
              data-inactive={!canScrollPrev || undefined}
              tabIndex={canScrollPrev ? 0 : -1}
              aria-orientation={orientation}
            >
              {previousControlIcon ? (
                previousControlIcon
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M224,128a8,8,0,0,1-8,8H59.31l58.35,58.34a8,8,0,0,1-11.32,11.32l-72-72a8,8,0,0,1,0-11.32l72-72a8,8,0,0,1,11.32,11.32L59.31,120H216A8,8,0,0,1,224,128Z"></path>
                </svg>
              )}
            </button>
            <button
              className={clsx(
                "ox-reset ox-CarouselControlNext",
                classNames?.controlNext,
              )}
              onClick={scrollNext}
              disabled={!canScrollNext || undefined}
              data-inactive={!canScrollNext || undefined}
              tabIndex={canScrollNext ? 0 : -1}
              aria-orientation={orientation}
            >
              {nextControlIcon ? (
                nextControlIcon
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"></path>
                </svg>
              )}
            </button>
          </div>
        ) : null}
      </div>
    </CarouselContext.Provider>
  );
};

const CarouselItem: React.FC<CarouselItemProps> = ({ className, ...props }) => {
  const { slidesPerView = 1, orientation, slideGap } = useCarouselContext();

  return (
    <div
      data-orientation={orientation}
      className={clsx("ox-reset ox-CarouselItem", className)}
      style={{
        flex: `0 0 ${100 / slidesPerView}%`,
        paddingInlineEnd: orientation === "horizontal" ? slideGap : undefined,
        paddingBottom: orientation === "vertical" ? slideGap : undefined,
      }}
      {...props}
    />
  );
};

Carousel.Item = CarouselItem;

Carousel.displayName = "Carousel";
Carousel.Item.displayName = "Carousel.Item";

export { Carousel };
export type {
  CarouselProps,
  CarouselItemProps,
  EmblaCarouselType as CarouselType,
};
